// BHPH (buy-here-pay-here) account positions, computed from saved deals and
// website payments. A deal is BHPH when its saved forms model says so
// (deals.forms_data.dealType === 'bhph' — written by the Deal Builder).
//
// Payments made outside the site (cash in office, money orders) are not in
// the `payments` table, so amounts here are a floor, not the ledger of
// record. Matching is by customer email, narrowed by vehicle when both rows
// carry one.

import { supabase } from '../lib/supabase';
import { effectiveDerived, PAYMENT_FREQUENCIES } from '../config/dealPacketForms';
import { advanceDueDate } from '../config/amortization';

const num = (v) => {
  const x = parseFloat(String(v ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(x) ? x : 0;
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

function paymentMatchesDeal(payment, deal) {
  const pe = (payment.customer_email || '').trim().toLowerCase();
  const de = (deal.customer_email || '').trim().toLowerCase();
  if (!pe || !de || pe !== de) return false;
  if (payment.vehicle_id && deal.vehicle_id && payment.vehicle_id !== deal.vehicle_id) return false;
  return true;
}

export function computeAccountPosition(deal, matchedPayments, today = new Date()) {
  const model = deal.forms_data;
  const derived = effectiveDerived(model);
  const m = model.money;
  const freq =
    PAYMENT_FREQUENCIES.find((f) => f.value === m.paymentFrequency) || PAYMENT_FREQUENCIES[0];
  const nPay = Math.max(Math.round(num(m.numPayments)), 0);
  const paymentAmount = num(derived.paymentAmount);
  const amountFinanced = num(derived.amountFinanced);
  const r = num(m.apr) / 100 / freq.periodsPerYear;
  const firstDue = parseISO(m.firstPaymentDate);

  const received = round2(matchedPayments.reduce((s, p) => s + num(p.amount), 0));
  const lastPaymentDate = matchedPayments.length
    ? matchedPayments
        .map((p) => new Date(p.created_at))
        .sort((a, b) => b - a)[0]
    : null;

  const base = {
    deal,
    vehicle: deal.vehicles_db2025 || null,
    amountFinanced,
    paymentAmount,
    term: nPay,
    frequency: freq.value,
    apr: num(m.apr),
    received,
    paymentsCount: matchedPayments.length,
    lastPaymentDate,
  };

  if (!firstDue || nPay <= 0 || paymentAmount <= 0) {
    return { ...base, hasSchedule: false, daysLate: 0, dueAmount: 0, nextDueDate: null, principalBalance: amountFinanced };
  }

  let periodsDue = 0;
  for (let i = 1; i <= nPay; i++) {
    if (advanceDueDate(null, freq.value, i - 1, firstDue) <= today) periodsDue = i;
    else break;
  }

  const paidPeriods = Math.min(Math.floor(received / paymentAmount), nPay);
  const dueAmount = round2(Math.max(periodsDue - paidPeriods, 0) * paymentAmount);
  const nextDueDate =
    paidPeriods < nPay ? advanceDueDate(null, freq.value, paidPeriods, firstDue) : null;
  const daysLate =
    dueAmount > 0 && nextDueDate && nextDueDate < today
      ? Math.floor((today - nextDueDate) / MS_PER_DAY)
      : 0;

  // Amortized balance after the payments actually covered
  const k = paidPeriods;
  const principalBalance = round2(
    Math.max(
      r > 0
        ? amountFinanced * Math.pow(1 + r, k) - paymentAmount * ((Math.pow(1 + r, k) - 1) / r)
        : amountFinanced - k * paymentAmount,
      0
    )
  );

  return { ...base, hasSchedule: true, periodsDue, paidPeriods, dueAmount, nextDueDate, daysLate, principalBalance };
}

export async function loadBhphAccounts() {
  const { data: deals, error } = await supabase
    .from('deals')
    .select('*, vehicles_db2025!vehicle_id (year, make, model, vin, stock_number)')
    .order('created_at', { ascending: false });
  if (error) throw error;

  const bhphDeals = (deals || []).filter(
    (d) => d.forms_data && d.forms_data.dealType === 'bhph'
  );

  const emails = [
    ...new Set(
      bhphDeals.map((d) => (d.customer_email || '').trim().toLowerCase()).filter(Boolean)
    ),
  ];

  let payments = [];
  if (emails.length) {
    const { data: pays, error: payErr } = await supabase
      .from('payments')
      .select('*')
      .eq('status', 'completed');
    if (!payErr && pays) {
      payments = pays.filter((p) =>
        emails.includes((p.customer_email || '').trim().toLowerCase())
      );
    }
  }

  return bhphDeals.map((deal) =>
    computeAccountPosition(deal, payments.filter((p) => paymentMatchesDeal(p, deal)))
  );
}
